// ATE-CR-117
import { useEffect, useId, useRef } from "react";
import type { SyntheticEvent } from "react";

import type { EscalationListItem } from "@/api/escalations.api";

export interface EscalationDetailDrawerProps {
  readonly open: boolean;
  readonly escalation: EscalationListItem;
  onClose(): void;
}

// ATE-CR-117
export function EscalationDetailDrawer({
  open,
  escalation,
  onClose,
}: EscalationDetailDrawerProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const headingId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // Native <dialog> fires `cancel` on ESC. Forward to onClose and prevent
  // default so parent state drives the close (keeps state in sync).
  const handleNativeCancel = (event: SyntheticEvent<HTMLDialogElement>): void => {
    event.preventDefault();
    onClose();
  };

  // ATE-CR-117 — task 11 FIX: drawer fields aligned to backend EscalationItem.
  const escalatedAt = new Date(escalation.escalated_at).toLocaleString();
  const expiresAt = new Date(escalation.held_expires_at).toLocaleString();

  return (
    <dialog
      ref={ref}
      role="dialog"
      aria-labelledby={headingId}
      onCancel={handleNativeCancel}
      className="escalation-drawer"
    >
      <div className="escalation-drawer__inner">
        <div className="escalation-drawer__header">
          <h2 id={headingId} className="escalation-drawer__heading">
            Escalation Details
          </h2>
          <button
            type="button"
            className="escalation-drawer__close btn btn--ghost"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <dl className="escalation-drawer__fields">
          <div className="escalation-drawer__field">
            <dt>Request ID</dt>
            <dd>
              <code>{escalation.request_id}</code>
            </dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Request Type</dt>
            <dd>{escalation.request_type}</dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Tenant ID</dt>
            <dd>
              <code>{escalation.tenant_id}</code>
            </dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Status</dt>
            <dd>
              <span className="badge badge--escalated">{escalation.status}</span>
            </dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Held Amount</dt>
            <dd>{escalation.held_amount}</dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Held Resource</dt>
            <dd>{escalation.held_resource}</dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Held Source</dt>
            <dd>{escalation.held_source}</dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Hold Expires</dt>
            <dd>{expiresAt}</dd>
          </div>
          <div className="escalation-drawer__field">
            <dt>Escalated At</dt>
            <dd>{escalatedAt}</dd>
          </div>
        </dl>
      </div>
    </dialog>
  );
}
